'use client';

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChatProductGridProps } from './types';
import { GridIcon, ListIcon, SparkleIcon, FilterIcon, ChatBubbleIcon } from '../search/icons';
import ProductCard from '../search/ProductGrid/ProductCard';
import ProductCardSkeleton from '../search/ProductGrid/ProductCardSkeleton';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { useExternalPricing, resolveCustomerPrice } from '@/lib/pricing/useExternalPricing';

/** Longest card entrance: the stagger caps at 600ms, plus the 400ms fade. */
const ENTRANCE_MS = 1100;
/** How long a card takes to glide to its new place when the order changes. */
const GLIDE_MS = 400;

type Place = { x: number; y: number; w: number; h: number };

/** Where each card of `scope` sits, relative to the content box `root` and
 *  without any running animation's offset (a card mid-entrance is still
 *  shifted down by its fade-up). */
function measurePlaces(root: HTMLElement, scope: HTMLElement): { width: number; places: Map<string, Place> } {
  const box = root.getBoundingClientRect();
  const places = new Map<string, Place>();
  scope.querySelectorAll<HTMLElement>('[data-product-id]').forEach((el) => {
    const r = el.getBoundingClientRect();
    const t = getComputedStyle(el).transform;
    const m = t && t !== 'none' ? new DOMMatrixReadOnly(t) : null;
    places.set(el.dataset.productId ?? '', {
      x: r.left - box.left - (m?.m41 ?? 0),
      y: r.top - box.top - (m?.m42 ?? 0),
      w: r.width,
      h: r.height,
    });
  });
  return { width: Math.round(box.width), places };
}

function ChatProductGrid({
  products,
  resultsKey,
  isLoading,
  totalCount,
  picks,
  picksPending = false,
  currentPage,
  totalPages,
  onPageChange,
  viewMode = 'grid',
  onViewModeChange,
  onOpenFilters,
  filtersOpen = false,
  activeFilterCount = 0,
  onToggleChat,
  chatOpen = false,
}: ChatProductGridProps) {
  const clientConfig = useClientConfig();
  // Client-branded chat avatar (e.g. syfak's Αριάδνη) replaces the sparkle in
  // the empty state. apiOrigin-prefixed for cross-origin embeds.
  const chatAvatarUrl = clientConfig.ui.chatAvatarUrl;
  const chatAvatarSrc = chatAvatarUrl
    ? chatAvatarUrl.startsWith('/')
      ? `${clientConfig.apiOrigin ?? ''}${chatAvatarUrl}`
      : chatAvatarUrl
    : undefined;
  const productIds = useMemo(() => products.map((p) => p.id), [products]);
  const { prices, loading: pricesLoading } = useExternalPricing(productIds, clientConfig);
  const externalPricingActive = !!(clientConfig.features?.externalPricing && clientConfig.externalPricing);
  const cardPricingProps = (productId: string | number, indexedModel?: string | null) => ({
    customerPrice: resolveCustomerPrice(externalPricingActive, prices, productId, indexedModel),
    // Per-product pending, same rule as ProductSection: prices stream in
    // batches, so an id absent from the map while a fetch is running just
    // hasn't been requested yet → show the skeleton, not a catalogue price
    // (which would change under the user) and not "unavailable" (a lie).
    loadingCustomerPrice: externalPricingActive && pricesLoading && !prices?.has(String(productId)),
  });

  // ── The picks row and a grid that never moves (2026-11-03) ──
  // The assistant's 2–3 picks get their own row above the grid. While its
  // reply is still being written, the row holds placeholder cards; the picks
  // fade into them when the reply is finished. The grid below shows the whole
  // result in the search's order from the first moment and is never re-sorted
  // (re-sorting it swapped cards under the shopper's eyes).
  // The entrance animation plays only for a NEW result set (search, filters,
  // page); a card re-inserted later keeps still instead of blinking.
  const idsKey = useMemo(() => products.map((p) => String(p.id)).sort().join(','), [products]);
  const setKey = resultsKey ?? idsKey;
  const picksKey = picksPending ? 'pending' : (picks ?? []).map((p) => p.id).join(',');
  const [settledSetKey, setSettledSetKey] = useState('');
  const entering = setKey !== settledSetKey;
  useEffect(() => {
    if (!entering) return;
    const t = setTimeout(() => setSettledSetKey(setKey), ENTRANCE_MS);
    return () => clearTimeout(t);
  }, [entering, setKey]);

  // The placeholders take the height of a real card, so the grid below does
  // not move when the picks replace them (card heights differ per client).
  const contentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const [slotHeight, setSlotHeight] = useState<number | null>(null);
  const measureSlot = () => {
    const first = mainRef.current?.querySelector<HTMLElement>('[data-product-id]');
    if (!first) return;
    const h = Math.round(first.getBoundingClientRect().height);
    setSlotHeight((prev) => (prev === h ? prev : h));
  };

  // When the picks row changes size (fewer picks than placeholders on a
  // phone, or no picks at all), the grid below glides to its new place
  // instead of jumping (FLIP: remember where each card was, start it there).
  const placesRef = useRef<{ layout: string; places: Map<string, Place> }>({ layout: '', places: new Map() });
  useLayoutEffect(() => {
    const root = contentRef.current;
    const scope = mainRef.current;
    if (!root || !scope) return;
    measureSlot();
    const { width, places } = measurePlaces(root, scope);
    const layout = `${setKey}|${viewMode}|${width}`;
    const before = placesRef.current;
    placesRef.current = { layout, places };
    if (before.layout !== layout || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll<HTMLElement>('[data-product-id]').forEach((el) => {
      const id = el.dataset.productId ?? '';
      const was = before.places.get(id);
      const now = places.get(id);
      if (!was || !now) return;
      const dx = was.x - now.x;
      const dy = was.y - now.y;
      if (Math.abs(dx) <= 0.5 && Math.abs(dy) <= 0.5) return;
      el.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
        { duration: GLIDE_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      );
    });
  }, [setKey, viewMode, picksKey]);

  // Cards change height after they first render (syfak's prices and tags load
  // a moment later, images settle): re-measure, so the placeholders match and
  // the next glide starts from where the cards really are.
  useEffect(() => {
    const root = contentRef.current;
    const scope = mainRef.current;
    if (!root || !scope || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      measureSlot();
      const { width, places } = measurePlaces(root, scope);
      placesRef.current = { layout: `${setKey}|${viewMode}|${width}`, places };
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, [setKey, viewMode]);

  // Empty state — no products yet
  if (!isLoading && products.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4 h-full bg-brand/5">
        {/* Below `md` the conversation slide-over sits over this, and the
            greeting is centred in a full-width container — so it can only ever
            be seen sliced in half behind the panel, which reads as broken. The
            tinted ground is kept (it still frames the sliver of grid at the
            left); the content is not. Unchanged at md+, where the chat is a
            column beside this rather than on top of it. */}
        <div className="hidden md:flex md:flex-col md:items-center md:gap-4">
        {chatAvatarSrc ? (
          <img src={chatAvatarSrc} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <SparkleIcon className="w-10 h-10" />
        )}
        <div className="flex flex-col items-center gap-1">
          <p className="text-[16px] font-gotham font-medium text-[#101828] text-center leading-[28px]">
            {chatAvatarSrc
              ? clientConfig.ui.strings.aiAssistantTitle
              : 'Γεια σου! Είμαι ο AI Βοηθός σου'}
          </p>
          <p className="text-[14px] font-gotham text-[#6A7282] text-center leading-[22px] max-w-[400px]">
            Μίλα μου φυσικά για το τι ψάχνεις και θα σε βοηθήσω να βρεις αυτό που χρειάζεσαι ή να σου δώσω ιδέες!
          </p>
        </div>
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (isLoading && products.length === 0) {
    return (
      <div className="p-6 @container">
        <div className="grid grid-cols-1 @[420px]:grid-cols-2 @[600px]:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <ProductCardSkeleton key={i} variant="grid" />
          ))}
        </div>
      </div>
    );
  }

  const showPagination = totalPages != null && totalPages > 1 && currentPage != null && onPageChange;

  const displayCount = totalCount ?? products.length;

  return (
    <div className="animate-fadeIn">
      {/* Header row */}
      {displayCount > 0 && (
        <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b-[0.5px] border-[#D8D8D8] bg-white sticky top-0 z-10">
          {/* Stacked title + count (Figma 525:12675) */}
          <div className="flex flex-col items-start min-w-0">
            <p className="text-[16px] font-bold font-gotham text-[#101828] leading-[28px]">Προϊόντα</p>
            <p className="text-[12px] font-normal font-gotham text-[#6A7282] leading-[20px] truncate">
              {!picksPending && picks && picks.length > 0 ? (
                <>{picks.length} {picks.length === 1 ? 'προτεινόμενο' : 'προτεινόμενα'} · {displayCount} συνολικά</>
              ) : (
                <>{displayCount} {displayCount === 1 ? 'προϊόν' : 'προϊόντα'}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Filters trigger — narrow chat (<xl), where the filters sidebar is
                hidden. Brand-coloured while the slide-over is open. */}
            {onOpenFilters && (
              <button
                onClick={onOpenFilters}
                aria-label={activeFilterCount > 0 ? `Φίλτρα (${activeFilterCount} ενεργά)` : 'Φίλτρα'}
                aria-expanded={filtersOpen}
                className={`relative xl:hidden flex items-center justify-center h-9 w-9 cursor-pointer transition-colors ${
                  filtersOpen ? 'bg-brand text-brand-text' : 'bg-black text-white hover:bg-black/85'
                }`}
              >
                <FilterIcon className="w-[18px] h-[18px]" color="currentColor" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-brand text-brand-text text-[10px] font-gotham font-bold leading-none ring-2 ring-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
            {/* Conversation toggle — phone only. Below md the chat is a
                slide-over rather than a column, so this is the way back to it;
                brand-coloured while it is open, mirroring the filters trigger. */}
            {onToggleChat && (
              <button
                onClick={onToggleChat}
                aria-label="Συνομιλία"
                aria-expanded={chatOpen}
                className={`md:hidden flex items-center justify-center h-9 w-9 cursor-pointer transition-colors ${
                  chatOpen ? 'bg-brand text-brand-text' : 'bg-black text-white hover:bg-black/85'
                }`}
              >
                <ChatBubbleIcon className="w-[18px] h-[18px]" color="currentColor" />
              </button>
            )}
            {onViewModeChange && (
              <>
                <button
                  onClick={() => onViewModeChange('grid')}
                  className={`transition-colors cursor-pointer ${viewMode === 'grid' ? 'text-brand' : ''}`}
                  aria-label="Grid view"
                >
                  <GridIcon className="w-6 h-6" color="currentColor" />
                </button>
                <button
                  onClick={() => onViewModeChange('list')}
                  className={`transition-colors cursor-pointer ${viewMode === 'list' ? 'text-brand' : ''}`}
                  aria-label="List view"
                >
                  <ListIcon className="w-6 h-6" color="currentColor" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Product content — @container so the grid column count tracks the
          actual products-pane width, not the viewport (the pane shrinks when
          the chat sidebar / filters sidebar are present). */}
      <div ref={contentRef} className="p-6 flex flex-col gap-4 @container">
        {(() => {
          const variant = viewMode === 'grid' ? 'grid' : 'list';
          const rowClass = variant === 'grid' ? 'grid grid-cols-1 @[420px]:grid-cols-2 @[600px]:grid-cols-3 gap-4' : '';
          const showPicksRow = products.length > 0 && (picksPending || (picks?.length ?? 0) > 0);
          return (
            <>
              {showPicksRow && (
                <div className={rowClass}>
                  {picksPending
                    ? [0, 1, 2].map((i) => (
                        <div key={`slot-${i}`} data-pick-slot className="overflow-hidden animate-pulse" style={slotHeight ? { height: slotHeight } : undefined} aria-hidden="true">
                          <ProductCardSkeleton variant={variant} />
                        </div>
                      ))
                    : picks!.map((product, index) => (
                        <ProductCard key={product.id} product={product} variant={variant} index={index} source="agent_chat" {...cardPricingProps(product.id, product.model)} />
                      ))}
                </div>
              )}
              {showPicksRow && (
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                  <span className="text-[12px] font-gotham text-[#99A1AF] whitespace-nowrap">
                    Όλα τα προϊόντα
                  </span>
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                </div>
              )}
              <div ref={mainRef} className={rowClass}>
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} variant={variant} index={index} source="agent_chat" animate={entering} {...cardPricingProps(product.id, product.model)} />
                ))}
              </div>
            </>
          );
        })()}

        {/* Pagination controls */}
        {showPagination && (
          <div className="flex items-center justify-center gap-3 pt-2 pb-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 rounded-lg text-[12px] font-gotham font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-brand hover:bg-brand/10 cursor-pointer"
            >
              ← Προηγούμενη
            </button>
            <span className="text-[12px] font-gotham text-[#6A7282]">
              Σελίδα {currentPage} από {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 rounded-lg text-[12px] font-gotham font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-brand hover:bg-brand/10 cursor-pointer"
            >
              Επόμενη →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Memoised: the parent re-renders for every update of the streaming reply;
// the grid only needs to when its own props change.
export default memo(ChatProductGrid);
